import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

/**
 * Étoile de favori, producteur ou produit.
 *
 * Un clic l’allume, un second la retire. L’état visuel est le même partout —
 * fiche, carte, préférences — pour que le geste soit reconnaissable.
 */
@Component({
  selector: 'ct-favorite-star',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      type="button"
      class="star"
      [class.on]="on()"
      [disabled]="busy()"
      [attr.aria-pressed]="on()"
      [attr.aria-label]="label()"
      (click)="onClick($event)"
    >
      <span aria-hidden="true">{{ on() ? '★' : '☆' }}</span>
    </button>
  `,
  styles: `
    :host {
      display: inline-flex;
    }

    .star {
      display: grid;
      place-items: center;
      width: 2rem;
      height: 2rem;
      padding: 0;
      border: 0;
      background: transparent;
      color: var(--ct-text-muted);
      font-size: 1.35rem;
      line-height: 1;
      cursor: pointer;
      border-radius: var(--ct-radius-sm);
      transition: color var(--ct-transition);

      &:hover:not(:disabled),
      &:focus-visible {
        color: var(--ct-or-recolte-dark);
      }

      &:focus-visible {
        outline: 2px solid var(--ct-focus);
        outline-offset: 2px;
      }

      &:disabled {
        cursor: wait;
        opacity: 0.65;
      }

      &.on {
        color: var(--ct-or-recolte-dark);
      }
    }
  `,
})
export class CtFavoriteStar {
  readonly on = input(false);
  readonly busy = input(false);
  readonly kind = input<'producer' | 'product'>('producer');

  readonly toggled = output<void>();

  protected readonly label = computed(() => {
    if (this.kind() === 'product') {
      return this.on() ? 'Retirer ce produit des favoris' : 'Ajouter ce produit aux favoris';
    }
    return this.on() ? 'Retirer ce producteur des favoris' : 'Ajouter ce producteur aux favoris';
  });

  protected onClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.busy()) {
      return;
    }
    this.toggled.emit();
  }
}
